import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, money, imgSrc } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader, PublicFooter, Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";

export default function AskLanding() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get(`/ask/${code}`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [code]);

  const resolveSource = () => {
    const src = searchParams.get("src");
    if (src === "qr") return "qr";
    if (src === "invite" || data?.kind === "invite") return "invite";
    return "web";
  };

  const start = async () => {
    if (!ready) return;
    const source = resolveSource();
    if (!user) {
      nav(`/login?redirect=${encodeURIComponent(`/ask/${code}${source === "qr" ? "?src=qr" : ""}`)}`);
      return;
    }
    setStarting(true);
    try {
      const { data: res } = await api.post(`/ask/${code}/start`, { source });
      toast.success(
        res.boost_bonus_cents
          ? "You're in — boost unlocked for your first conversion"
          : "You're in — start sharing",
      );
      nav(res.refer_path || `/app/refer/${res.branch_id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setStarting(false);
    }
  };

  if (data === null || !ready) {
    return (
      <div className="flex min-h-screen flex-col">
        <PublicHeader />
        <Loading />
        <PublicFooter />
      </div>
    );
  }
  if (data === false) {
    return (
      <div className="flex min-h-screen flex-col">
        <PublicHeader />
        <p className="flex-1 p-10 text-center text-muted-foreground">This ask link is invalid.</p>
        <PublicFooter />
      </div>
    );
  }

  const expired = data.expired || data.status === "expired";
  const branch = data.branch || {};
  const biz = data.company_name || branch.company_name || "this business";
  const hero = branch.portfolio_image_urls?.[0] || branch.logo_url;

  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-4 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Ask for referrals</p>
        <h1 className="mt-3 text-3xl font-black tracking-tight" data-testid="ask-prompt">
          {data.prompt}
        </h1>

        <div
          className="mt-6 overflow-hidden rounded-[14px] border border-border bg-card"
          data-testid="ask-business-card"
        >
          {hero ? (
            <img
              src={imgSrc(hero)}
              alt=""
              className="h-36 w-full object-cover"
            />
          ) : (
            <div className="flex h-28 items-center justify-center bg-brand-green/10 text-3xl font-black text-brand-green">
              {(biz || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="space-y-1 p-4">
            <p className="text-lg font-bold">{biz}</p>
            {branch.address ? (
              <p className="text-sm text-muted-foreground">{branch.address}</p>
            ) : null}
            {branch.subcategory_badge || branch.subcategory_name ? (
              <p className="text-xs font-semibold text-brand-green">
                {branch.subcategory_badge || branch.subcategory_name}
              </p>
            ) : null}
          </div>
        </div>

        <p className="mt-4 text-muted-foreground">
          {expired
            ? "This invite has expired."
            : data.boost_bonus_cents > 0
              ? `Earn ${money(data.earn_cents)} on your first conversion (${money(data.standard_reward_cents)} standard + ${money(data.boost_bonus_cents)} bonus), then the standard reward after that.`
              : `Earn ${money(data.earn_cents)} per customer you send.`}
        </p>
        <Button
          className="mt-8 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark"
          disabled={expired || starting}
          onClick={start}
          data-testid="ask-start-btn"
        >
          {starting
            ? "Starting…"
            : expired
              ? "Invite expired"
              : `Start referring ${biz}`}
        </Button>
      </main>
      <PublicFooter />
    </div>
  );
}
